import { Card, SectionLabel, Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { deriveOrgInitials } from '@kk/domain';
import { useState } from 'react';
import { useOutletContext } from 'react-router';

import { useMembership, useSession } from '@/auth/SessionProvider';
import { ColumnHead, LinkButton, Status } from '@/components/bits';
import { CodeOnHoldNote, useCodesOnHold } from '@/components/CodeOnHold';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import {
  useBilling,
  useBillingRedirect,
  useErrorMessage,
  useGroups,
  useRemoveStaff,
  useStaff,
  useUpdateOrg,
} from '@/data/hooks';
import { monthDay } from '@/lib/time';
import type { ShellContext } from '@/shell/Shell';
import type { OrgGroupDetail, OrgSummary, StaffMember } from '@/types/wire';

/** Two letters from a name, for the row's disc — the same shape the roster uses for a child. */
function initialsOf(member: StaffMember): string {
  const source = member.displayName ?? member.email ?? '';
  const words = source.match(/[\p{L}\p{N}]+/gu) ?? [];
  return words
    .slice(0, 2)
    .map((w) => w[0]!)
    .join('')
    .toUpperCase();
}

/**
 * **THE CLUB — three names, three jobs, each stated where it is edited.**
 *
 * Nobody would guess full-name from short-form from badge-letters looking at three adjacent text boxes, and
 * the club that mistypes its own name is otherwise stuck with a wrong monogram on a nine-year-old's badge
 * shelf. The disc beside the letters renders LIVE because this is the only field whose effect is invisible
 * from this screen — and while the box is empty it shows the DERIVED letters, so "BG" for Boys & Girls Club
 * reads as the bad default it is rather than as nothing at all.
 */
function ClubSection({ org }: { org: OrgSummary }) {
  const errorMessage = useErrorMessage();
  const update = useUpdateOrg();
  const [name, setName] = useState(org.name);
  const [shortName, setShortName] = useState(org.shortName ?? '');
  const [initials, setInitials] = useState(org.initials ?? '');
  const [error, setError] = useState<string | null>(null);

  // what the disc will say once saved: the typed letters, or the ones the name derives when it is empty
  const disc = initials.trim().toUpperCase() || deriveOrgInitials(name || org.name);
  const dirty =
    name !== org.name || shortName !== (org.shortName ?? '') || initials !== (org.initials ?? '');

  const save = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        name: name.trim(),
        // an empty box CLEARS the field; `null` is how the wire says so, and an omitted key means "leave it"
        shortName: shortName.trim() || null,
        initials: initials.trim() || null,
      });
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <section className="stack" data-testid="club-section">
      <SectionLabel>{deck.o08.club}</SectionLabel>
      <Card variant="quiet" radius="row" padding={18}>
        <div className="stack">
          <TextField
            label={deck.o08.fullName}
            value={name}
            onChangeText={setName}
            testID="club-name"
          />
          <TextField
            label={deck.o08.shortForm}
            value={shortName}
            onChangeText={setShortName}
            testID="club-short-name"
          />
          <div className="badge-letters-row">
            <TextField
              label={deck.o08.badgeLetters}
              value={initials}
              onChangeText={setInitials}
              testID="club-initials"
            />
            {/* the live disc: the one field whose effect cannot be seen from this screen */}
            <div className="badge-disc" data-testid="badge-disc" aria-hidden>
              {disc}
            </div>
          </div>
          <Text variant="bodySm" tone="muted">
            {deck.o08.namesHelper}
          </Text>
          {error ? <Status testID="club-error">{error}</Status> : null}
          {dirty ? (
            <div className="actions">
              <WasabiButton
                label={placeholders.settings.save}
                size="sm"
                fullWidth={false}
                onPress={save}
                testID="save-club"
              />
            </div>
          ) : null}
        </div>
      </Card>
    </section>
  );
}

/**
 * **THE STAFF LIST, and the row that has no Remove.**
 *
 * Removal is a quiet link rather than a red button, and it ends access immediately — no flag and no grace
 * period, because a departed employee keeps seeing children's first names for exactly as long as a soft
 * delete says they may. The line beneath answers the question that stops a director pressing it.
 *
 * **The last org admin draws an em dash, not a disabled Remove** — the same rule as the last parent on P24b,
 * and the same reason this product deletes dead controls rather than greying them. The API refuses it too;
 * this is the screen agreeing with the server rather than the only thing standing in the way.
 *
 * There is no role control, deliberately: roles are set at invite and are not editable, so a dropdown would
 * be the third half-built thing in this feature. The column STATES the role because a director must read it.
 */
function StaffSection({ myEmail }: { myEmail?: string }) {
  const errorMessage = useErrorMessage();
  const staff = useStaff();
  const remove = useRemoveStaff();
  /** The row whose Remove was pressed and is waiting on Cancel / Remove (the 23 Oct audit, T1-5). */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const members = staff.data?.members ?? [];
  const admins = members.filter((m) => m.role === 'orgAdmin').length;

  const drop = async (id: string) => {
    setError(null);
    try {
      await remove.mutateAsync(id);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setConfirming(null);
    }
  };

  return (
    <section className="stack" data-testid="staff-section">
      <div className="page-header">
        <SectionLabel>{deck.o08.staff(members.length)}</SectionLabel>
      </div>
      <Card variant="quiet" radius="row" padding={18}>
        <div className="stack">
          <div className="staff-head">
            <ColumnHead>{deck.o08.name}</ColumnHead>
            <ColumnHead>{deck.o08.role}</ColumnHead>
          </div>
          {members.map((m) => {
            // "· you" by ADDRESS: a membership carries no user id, and the staff row's email is the one
            // the invite was accepted at, which is the same address this session signed in with
            const you = !!myEmail && m.email?.toLowerCase() === myEmail.toLowerCase();
            // the last org admin cannot be removed — by this screen or by the API, which refuses it too
            const last = m.role === 'orgAdmin' && admins <= 1;
            return (
              <div className="staff-row" key={m.id} data-testid={`staff-${m.id}`}>
                <div className="staff-who">
                  <div className="initials" aria-hidden>
                    {initialsOf(m)}
                  </div>
                  <div className="stack-tight">
                    <Text variant="body" tone="ink">
                      {m.displayName ?? m.email ?? ''}
                    </Text>
                    {m.email ? (
                      <Text variant="bodySm" tone="muted">
                        {m.email}
                      </Text>
                    ) : null}
                  </div>
                </div>
                <Text variant="bodySm" tone="muted">
                  {deck.o08.roleValue(m.role, m.groupIds.length, you)}
                </Text>
                {last ? (
                  <Text variant="bodySm" tone="muted" testID={`no-remove-${m.id}`}>
                    {deck.o08.noRemove}
                  </Text>
                ) : confirming === m.id ? null : (
                  <LinkButton
                    label={deck.o08.remove}
                    onPress={() => setConfirming(m.id)}
                    testID={`remove-${m.id}`}
                  />
                )}
                {/*
                 * **ONE TAP USED TO END A COLLEAGUE'S ACCESS** (the 23 Oct audit, T1-5). Now it asks first, the way
                 * Revoke code does on Groups: the question, then Cancel and Remove. The question is Team's own (O17).
                 */}
                {confirming === m.id ? (
                  <div className="confirm-row" data-testid={`remove-confirm-${m.id}`}>
                    <Text variant="bodySm" tone="ink">
                      {deck.o17.confirmRemove(m.displayName ?? m.email ?? '')}
                    </Text>
                    <div className="button-pair">
                      <WasabiButton
                        label={placeholders.deny.cancel}
                        variant="outline"
                        textSize="sm"
                        faceHeight={44}
                        onPress={() => setConfirming(null)}
                        testID={`remove-cancel-${m.id}`}
                      />
                      <WasabiButton
                        label={deck.o08.remove}
                        size="sm"
                        loading={remove.isPending}
                        onPress={() => void drop(m.id)}
                        testID={`remove-yes-${m.id}`}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
          <Text variant="bodySm" tone="muted">
            {deck.o08.removalHelper}
          </Text>
          {error ? <Status testID="staff-error">{error}</Status> : null}
        </div>
      </Card>
    </section>
  );
}

/** Groups and their codes, read-only here — the controls live on O05, which this does not duplicate. */
function GroupsSection({ groups }: { groups: OrgGroupDetail[] }) {
  // DECISIONS 93.2: dimmed with why while billing stops codes working; Set Up Billing scrolls to the section above
  const onHold = useCodesOnHold();
  const toBilling = () => document.getElementById('billing')?.scrollIntoView({ behavior: 'smooth' });
  return (
    <section className="stack" data-testid="groups-section">
      <SectionLabel>{deck.o08.groupsAndCodes}</SectionLabel>
      {groups.map((g) => (
        <Card variant="quiet" radius="row" padding={18} key={g.id}>
          <div className="staff-row" data-testid={`settings-group-${g.id}`}>
            <div className="stack-tight">
              <Text variant="body" tone="ink">
                {g.name}
              </Text>
              <Text variant="bodySm" tone="muted">
                {deck.o08.children(g.childCount)}
              </Text>
            </div>
            <div
              className={`stack-tight${onHold && g.joinCode?.active ? ' on-hold' : ''}`}
              data-testid={`settings-code-${g.id}`}
              aria-disabled={onHold && g.joinCode?.active ? true : undefined}
            >
              {g.joinCode?.active ? (
                <>
                  <Text variant="body" tone="ink">
                    {g.joinCode.code}
                  </Text>
                  {g.joinCode.expiresAt ? (
                    <Text variant="bodySm" tone="muted">
                      {deck.o08.expires(monthDay(g.joinCode.expiresAt))}
                    </Text>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
          {onHold && g.joinCode?.active ? (
            <CodeOnHoldNote reason={onHold} onSetUp={toBilling} testID={`settings-on-hold-${g.id}`} />
          ) : null}
        </Card>
      ))}
    </section>
  );
}

/**
 * **O08 · /settings — the club, its staff, its codes. Org admins only.**
 *
 * The tab was out of the nav for a week because it had nothing it could honestly carry. Every field on it now
 * has a write behind it — `updateOrg` for the three names, `removeStaff` for the list, and the group and code
 * routes that already existed. Branches are absent: the model carries one and no route creates or edits them.
 */
/**
 * **BILLING (DECISIONS 90) — drawn only while CLUB_BILLING is open.** The club pays on Stripe's own pages, so this
 * section says where things stand and sends the director there: Pay Monthly or Pay Yearly before billing exists,
 * Manage Billing after. A comped club sees one line and no buttons; a sponsor's share is named.
 */
function BillingSection() {
  const errorMessage = useErrorMessage();
  const billing = useBilling();
  const redirect = useBillingRedirect();
  const [error, setError] = useState<string | null>(null);
  const b = billing.data;
  if (!b?.enabled) return null;
  const go = async (to: 'monthly' | 'yearly' | 'manage') => {
    setError(null);
    try {
      await redirect.mutateAsync(to);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const dollars = `$${(b.thisMonth.estimateCents / 100).toFixed(2).replace(/\.00$/, '')}`;
  const copy = placeholders.billing;
  const line =
    b.status === 'paused'
      ? copy.paused
      : b.status === 'past_due'
        ? copy.pastDue
        : b.comped
          ? copy.comped
          : b.status === 'active'
            ? b.plan === 'yearly'
              ? copy.yearly
              : copy.monthly
            : copy.setUp;
  const hasBilling = b.status === 'active' || b.status === 'past_due' || b.status === 'paused';
  return (
    <section className="stack" id="billing" data-testid="billing-section">
      <SectionLabel>{copy.label}</SectionLabel>
      <Card variant="quiet" radius="row" padding={18}>
        <div className="stack">
          <Text variant="body" tone="ink" testID="billing-line">
            {line}
          </Text>
          {b.comped ? null : (
            <Text variant="bodySm" tone="muted" testID="billing-month">
              {copy.thisMonth(b.thisMonth.connected, dollars)}
            </Text>
          )}
          {b.sponsoredChildren > 0 ? (
            <Text variant="bodySm" tone="muted" testID="billing-sponsor">
              {copy.sponsored(b.sponsor, b.sponsoredChildren)}
            </Text>
          ) : null}
          {b.priceLockUntil ? (
            <Text variant="bodySm" tone="muted">
              {copy.priceLock(`${monthDay(b.priceLockUntil)} ${new Date(b.priceLockUntil).getFullYear()}`)}
            </Text>
          ) : null}
          {b.status === 'paused' && b.deletesOn ? (
            <Text variant="bodySm" tone="muted" testID="billing-deletes">
              {copy.deletesOn(`${monthDay(b.deletesOn)} ${new Date(b.deletesOn).getFullYear()}`)}
            </Text>
          ) : null}
          {error ? <Status testID="billing-error">{error}</Status> : null}
          {b.comped ? null : (
            <div className="actions">
              {hasBilling ? (
                <WasabiButton label={copy.manage} size="sm" fullWidth={false} onPress={() => go('manage')} testID="billing-manage" />
              ) : (
                <>
                  <WasabiButton label={copy.payMonthly} size="sm" fullWidth={false} onPress={() => go('monthly')} testID="billing-monthly" />
                  <WasabiButton label={copy.payYearly} size="sm" fullWidth={false} onPress={() => go('yearly')} testID="billing-yearly" />
                </>
              )}
            </div>
          )}
          {/* DECISIONS 92: once counsel publishes the Club Terms, Stripe's checkout asks for them; this says so first */}
          {b.termsUrl && !b.comped ? (
            <Text variant="bodySm" tone="muted" testID="billing-terms">
              {copy.termsLine}{' '}
              <a href={b.termsUrl} target="_blank" rel="noopener noreferrer" data-testid="billing-terms-link">
                {copy.termsLink}
              </a>
            </Text>
          ) : null}
        </div>
      </Card>
    </section>
  );
}

export function SettingsScreen() {
  const errorMessage = useErrorMessage();
  const { org } = useOutletContext<ShellContext>();
  const membership = useMembership();
  const { session } = useSession();
  const orgAdmin = membership?.role === 'orgAdmin';
  const groups = useGroups(orgAdmin);

  if (!orgAdmin) return <Status testID="org-admin-only">{placeholders.groups.orgAdminOnly}</Status>;
  // the shell resolves the org before its children render; a missing one is the shell's own status line
  if (!org) return <Status testID="settings-loading">{placeholders.empty.loading}</Status>;

  const rows = groups.data ?? [];
  const status = groups.isPending
    ? placeholders.empty.loading
    : groups.error
      ? errorMessage(groups.error)
      : null;

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{deck.o08.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {deck.o08.subtitle}
          </Text>
        </div>
      </div>
      <ClubSection org={org} />
      <BillingSection />
      <StaffSection myEmail={session?.user.email} />
      {status ? <Status>{status}</Status> : <GroupsSection groups={rows} />}
    </>
  );
}
